// Mirrors LoyaltyLion's off-screen rewards list into the theme's stepper markup.
// LoyaltyLion renders twice — guest, then authenticated — replacing its own
// container, so nothing here holds a reference into its DOM.

class UtilsReward {
  static Status = Object.freeze({
    ENABLED: "enabled",
    PENDING: "pending"
  })
  static removeTrailingZeros(price) {
    const parts = price.split('.');
    if (parts.length === 2 && parts[1] === '00') {
        return parts[0];
    } 
      return price; 
  }
  static addThousandsSeparator(number,separator = ',') {
    return  number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, separator);
  }
  static calculateStepRatio(points,steps) {
    if(points < steps[0]) return 0; 
     for (let i = 1; i < steps.length; i++) {
      if (points <= steps[i]) {
        const rangeStart = steps[i - 1];
        const rangeEnd = steps[i];
        const range = rangeEnd - rangeStart;
        const pointsInRange = points - rangeStart;
        const ratio = (pointsInRange / range) + (i - 1);
        return ratio / (steps.length - 1);
      }
    }
    return 1; // If points exceed the max value, return 1 (100%)
}
  // null = unknown (guest, or pre-auth). Zero is a real balance.
  static customerPoints() {
    const points = window.loyaltylion?.customer?.pointsRedeemable;
    return typeof points === 'number' ? points : null;
  }
}

class RewardModel{
  constructor(reward, customerPoints){
      this.id = reward.id;
      this.sortKey = reward.sort_key;
      this.points= reward.point_cost;
      this.discount= reward.discount_amount * 100;
      this.method= reward.method;
      this.status = customerPoints === null || customerPoints >= reward.point_cost
        ? UtilsReward.Status.ENABLED
        : UtilsReward.Status.PENDING;
  }
}

const selectors = {
  template:'[data-template="reward-item"]',
  placeholder:'[data-lion-rewards-list]',
  rewardsList:'.rewards-list',
  lionRewardItem:'.lion-reward-item',
  lionActionButton:'.lion-action-button',
  templateNode: (type)=> '[data-reward-item="'+ type + '"]',
  buttonDisabled: 'lion-action-button--disabled',
}

class RewardsList extends HTMLElement {

  constructor() {
    super();
    this.itemsById = new Map();
  }

  connectedCallback(){
    if (this.observer) return;
    this.template = this.querySelector(selectors.template)?.content.firstElementChild;
    this.listEl = this.querySelector(selectors.rewardsList);
    this.placeholder = this.querySelector(selectors.placeholder);
    if (!this.template || !this.listEl || !this.placeholder) return;
    this.fallbackLabel = this.template
      .querySelector(selectors.templateNode('button'))
      ?.textContent.trim() ?? '';
    this.observer = new MutationObserver(() => this.sync());
    this.observer.observe(this.placeholder, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
    this.sync();
    // The balance can land without any change to LoyaltyLion's markup.
    window.LoyaltyTier?.whenReady().then(() => this.sync()).catch(() => {});
  }

  disconnectedCallback(){
    this.observer?.disconnect();
    this.observer = null;
  }

  sync(){
    const rewards = window.loyaltylion?.program?.rewards;
    if(!rewards?.length) return;
    const customerPoints = UtilsReward.customerPoints();
    const models = rewards
      .map(reward => new RewardModel(reward, customerPoints))
      .sort((a, b) => a.sortKey - b.sortKey);
    models.forEach(reward => this.renderReward(reward));
    this.setProgress(models, customerPoints);
  }

  renderReward(reward){
    let itemEl = this.itemsById.get(reward.id);
    if(!itemEl){
      itemEl = this.buildItem(reward);
      this.itemsById.set(reward.id, itemEl);
      this.listEl.append(itemEl);
    }
    this.setButton(itemEl.querySelector(selectors.templateNode('button')), reward.id);
    this.setStepStatus(itemEl.querySelector(selectors.templateNode('step')), reward.status);
  }

  // Listener attached once here — re-syncs would otherwise stack them up.
  buildItem(reward){
    const itemEl = this.template.cloneNode(true);
    this.setDiscount(itemEl,reward.discount);
    this.setMethod(itemEl,reward.method);
    this.setPoints(itemEl,reward.points);
    itemEl
      .querySelector(selectors.templateNode('button'))
      .addEventListener('click', () => this.claim(reward.id));
    return itemEl;
  }

  claim(rewardId){
    const lionButton = this.lionButtonFor(rewardId);
    if(lionButton) {
      lionButton.click();
      return;
    }
    this.sync();
  }

  lionButtonFor(rewardId){
    const itemEl = this.placeholder.querySelector(
      selectors.lionRewardItem + '[data-reward-id="' + rewardId + '"]'
    );
    return itemEl?.querySelector(selectors.lionActionButton) ?? null;
  }

  setProgress(rewards,customerPoints){
    const points = rewards.map(r=>r.points);
    const progress = customerPoints === null ? 1 : UtilsReward.calculateStepRatio(customerPoints,points);
    this.style.setProperty('--progress-ratio',progress);
  }

  setStepStatus(el,status){
    if(status === UtilsReward.Status.ENABLED){
      el.classList.add('enabled');
    } else{
      el.classList.remove('enabled');
    }
  }

  setDiscount(el,discount){
    const discountEl = el.querySelector(selectors.templateNode('discount'));
    const discountFormatted = (window.Shopify && Shopify.formatMoney)
      ? Shopify.formatMoney(discount, window.money_format)
      : '£' + (discount / 100).toFixed(2);
    discountEl.textContent = UtilsReward.removeTrailingZeros(discountFormatted).replace(",00", "");
  }
  setMethod(el,method){
    const methodEl = el.querySelector(selectors.templateNode('method'));
    methodEl.textContent = method.replace('_',' ').toUpperCase();
  }
  setPoints(el,points){
    const pointsEl = el.querySelector(selectors.templateNode('points'));
    pointsEl.textContent = UtilsReward.addThousandsSeparator(points) + ' POINTS'
  }
  // LoyaltyLion owns the label and enabled state — it also locks rewards for
  // stock, claim limits and redemption caps, which the balance can't tell us.
  setButton(el,rewardId){
    const refButton = this.lionButtonFor(rewardId);
    if(!refButton){
      if(!el.textContent.trim()) el.textContent = this.fallbackLabel;
      el.disabled = true;
      return;
    }
    el.innerHTML = refButton.innerHTML;
    if(!el.textContent.trim()) el.textContent = this.fallbackLabel;
    el.disabled = refButton.classList.contains(selectors.buttonDisabled)
      || refButton.getAttribute('aria-disabled') === 'true';
  }
}

customElements.define("rewards-list", RewardsList);
